import { closeNumbers } from './line';
import C from '../complex/math-complex';

export interface CubicBezierControlPoints {
	p0: number[],
	p1: number[],
	p2: number[],
	p3: number[],
	dT: number
}

/**
 * A Cubic Bezier curve
 * https://en.wikipedia.org/wiki/Bézier_curve
 * https://en.wikipedia.org/wiki/Cubic_function
 */
export class CubicBezier {

	public p0: number[]; //the start point of the curve
	public p1: number[]; //control point 1
	public p2: number[]; //control point 2
	public p3: number[]; //the end point of the curve
	public dT: number; //the time it takes to travel the line from start to end.

	private a: number[];
	private b: number[];
	private c: number[];
	private d: number[];

	//            public stroke: Stroke;
	public classification: number;

	/**
	 * 
	 * @param p0 the start point of the curve
	 * @param p1 control point 1
	 * @param p2 control point 2
	 * @param p3 the end point of the curve
	 * @param dT the time it takes to travel the line from start to end.
	 */
	constructor(p0: number[], p1: number[], p2: number[], p3: number[], dT: number = 1.0) {
		this.p0 = p0;
		this.p1 = p1;
		this.p2 = p2;
		this.p3 = p3;
		this.dT = dT;
		this.a = undefined;
	}
	/**
	 * Returns the position on the curve after 'travelling' a given amount of time where p = p0 for t=0 and p = p3 for t = dT.
	 * @param _t the moment where to get the position for 
	 */
	public get(_t: number): number[] {
		let t: number = _t / this.dT;
		let T: number = t * t;
		let tInv: number = 1 - t;
		let Tinv: number = tInv * tInv;

		let c0: number = Tinv * tInv;
		let c1: number = 3 * Tinv * t;
		let c2: number = 3 * tInv * T;
		let c3: number = T * t;

		let x: number = c0 * this.p0[0] + c1 * this.p1[0] + c2 * this.p2[0] + c3 * this.p3[0];
		let y: number = c0 * this.p0[1] + c1 * this.p1[1] + c2 * this.p2[1] + c3 * this.p3[1];
		return [x, y];
	}
	public getDerivative(_t: number): number[] {
		let t: number = _t / this.dT;
		let T: number = t * t;
		let tInv: number = 1 - t;
		let Tinv: number = tInv * tInv;

		let c0: number = 3 * Tinv;
		let c1: number = 6 * tInv * t;
		let c2: number = 3 * T;

		return [
			c0 * (this.p1[0] - this.p0[0]) + c1 * (this.p2[0] - this.p1[0]) + c2 * (this.p3[0] - this.p2[0]),
			c0 * (this.p1[1] - this.p0[1]) + c1 * (this.p2[1] - this.p1[1]) + c2 * (this.p3[1] - this.p2[1])
		]
	}
	public getSecondDerivative(_t: number): number[] {
		let t: number = _t / this.dT;
		let tInv: number = 1 - t;

		let c0: number = 6 * tInv;
		let c1: number = 6 * t;

		return [
			c0 * (this.p2[0] - 2 * this.p1[0] + this.p0[0]) + c1 * (this.p3[0] - 2 * this.p2[0] + this.p1[0]),
			c0 * (this.p2[1] - 2 * this.p1[1] + this.p0[1]) + c1 * (this.p3[1] - 2 * this.p2[1] + this.p1[1])
		]
	}
	private prepareInverse(): void {
		if (this.a === undefined) {
			this.a = [(3 * this.p1[0] - 3 * this.p2[0] + this.p3[0] - this.p0[0]), (3 * this.p1[1] - 3 * this.p2[1] + this.p3[1] - this.p0[1])];
			this.b = [(3 * this.p2[0] - 6 * this.p1[0] + 3 * this.p0[0]), (3 * this.p2[1] - 6 * this.p1[1] + 3 * this.p0[1])];
			this.c = [(3 * this.p1[0] - 3 * this.p0[0]), (3 * this.p1[1] - 3 * this.p0[1])];
			this.d = [this.p0[0], this.p0[1]];
		}
	}
	public inv(value: number, axisi = 1): number[] {
		this.prepareInverse();
		let a = this.a[axisi];
		let b = this.b[axisi];
		let c = this.c[axisi];
		let d = this.d[axisi] - value;

		let t: number[];

		if (!closeNumbers(a, 0)) {
			//we have a true cubic equation: see https://en.wikipedia.org/wiki/Cubic_function
			let delta = 18 * a * b * c * d - 4 * b * b * b * d + b * b * c * c - 4 * a * c * c * c - 27 * a * a * d * d; //discriminant
			let delta0 = b * b - 3 * a * c;
			let delta1 = 2 * b * b * b - 9 * a * b * c + 27 * a * a * d;


			if (delta == 0) { //special casses
				if (delta0 == 0) {
					t = [-b / (3 * a)]; // a triple root, the indlection point
				} else {
					t = [(9 * a * d - b * c) / (2 * delta0), (4 * a * b * c - 9 * a * a * d - b * b * b) / (a * delta0)]; //first is a double root, second is simple root
				}
			} else {
				let termA: C = C.g(delta1 / 2, 0);
				let termB: C = C.g(delta1 * delta1 - 4 * delta0 * delta0 * delta0, 0).sqrt().div(C.g(2, 0));
				let cubicRootOfUnity = C.g(-0.5, Math.sqrt(3) / 2);

				let C0 = (delta0 == 0 && delta1 < 0) ? termA.sub(termB).sqrtCube() : termA.add(termB).sqrtCube();

				let Croots = [CubicBezier.getRoot(a, b, delta0, C0)];
				Croots.push(CubicBezier.getRoot(a, b, delta0, C0.mult(cubicRootOfUnity)));
				Croots.push(CubicBezier.getRoot(a, b, delta0, C0.mult(cubicRootOfUnity.conj())));
				//                      console.log('complex roots=%s',Croots);
				t = [Croots[0].r, Croots[1].r, Croots[2].r];
			}
		} else if (!closeNumbers(b, 0)) {
			//we have a true quadratic equation:
			let discriminant = c * c - 4 * b * d;

			if (discriminant == 0) {//special case
				t = [-c / (2 * b)];
			} else if (discriminant < 0.0) {
				t = [];
			} else {
				t = [(-c + discriminant) / (2 * b), (-c - discriminant) / (2 * b)];
			}
		} else if (!closeNumbers(c, 0)) {
			//we have a true linear equation
			t = [-d / c];
		} else {
			//we have a true constant equation
			t = [];
		}
		//remove all t's not between 0 and 1
		for (let n = 0; n < t.length;) {
			if (t[n] < 0 || t[n] > 1) {
				t.splice(n, 1);
			} else {
				n++;
			}
		}
		t.sort((a, b) => { return a - b; });
		for (let n = 0; n < t.length; n++) {
			t[n] * this.dT;
		}
		return t;
	}
	private static getRoot(a: number, b: number, delta0: number, c: C) {
		return c.add((C.g(delta0, 0)).div(c)).add(C.g(b, 0)).div(C.g(-3 * a, 0));
	}

	/**
	 * Converts this curve to a curve that is defined from p0 towards p(t)
	 * and returns a new curve that is defined from p(t) towards point p3.
	 * @param _t the moment where a split should occur
	 */
	public split(_t: number): CubicBezier {

		let splittedControlPoints = CubicBezier.splitCurve(this.getControlPoints(), _t / this.dT);

		var cb = new CubicBezier(
			splittedControlPoints.c1.p0,
			splittedControlPoints.c1.p1,
			splittedControlPoints.c1.p2,
			splittedControlPoints.c1.p3,
			splittedControlPoints.c1.dT);

		this.p0 = splittedControlPoints.c0.p0;
		this.p1 = splittedControlPoints.c0.p1;
		this.p2 = splittedControlPoints.c0.p2;
		this.p3 = splittedControlPoints.c0.p3;
		this.dT = splittedControlPoints.c0.dT;
		this.a = undefined;

		return cb;
	}

	public getControlPoints(): CubicBezierControlPoints {
		return { p0: this.p0, p1: this.p1, p2: this.p2, p3: this.p3, dT: this.dT }
	}

	public static splitCurve(curve: CubicBezierControlPoints, t: number): { c0: CubicBezierControlPoints, c1: CubicBezierControlPoints } {
		let xCBb0p1: number = (curve.p1[0] - curve.p0[0]) * t + curve.p0[0];
		let yCBb0p1: number = (curve.p1[1] - curve.p0[1]) * t + curve.p0[1];

		let xHelper: number = (curve.p2[0] - curve.p1[0]) * t + curve.p1[0];
		let yHelper: number = (curve.p2[1] - curve.p1[1]) * t + curve.p1[1];

		let xCBb1p2: number = (curve.p3[0] - curve.p2[0]) * t + curve.p2[0];
		let yCBb1p2: number = (curve.p3[1] - curve.p2[1]) * t + curve.p2[1];

		let xCBb0p2: number = (xHelper - xCBb0p1) * t + xCBb0p1;
		let yCBb0p2: number = (yHelper - yCBb0p1) * t + yCBb0p1;

		let xCBb1p1: number = (xCBb1p2 - xHelper) * t + xHelper;
		let yCBb1p1: number = (yCBb1p2 - yHelper) * t + yHelper;

		let xSlice: number = (xCBb1p1 - xCBb0p2) * t + xCBb0p2;
		let ySlice: number = (yCBb1p1 - yCBb0p2) * t + yCBb0p2;

		return {
			c0: {
				p0: [curve.p0[0], curve.p0[1]],
				p1: [xCBb0p1, yCBb0p1],
				p2: [xCBb0p2, yCBb0p2],
				p3: [xSlice, ySlice],
				dT: t * curve.dT
			},
			c1: {
				p0: [xSlice, ySlice],
				p1: [xCBb1p1, yCBb1p1],
				p2: [xCBb1p2, yCBb1p2],
				p3: [curve.p3[0], curve.p3[1]],
				dT: (1.0 - t) * curve.dT
			}
		};
	}

	public static flatness(curve: CubicBezierControlPoints): number {
		return Math.max(
			Math.pow(3.0 * curve.p1[0] - 2.0 * curve.p0[0] - curve.p3[0], 2.0),
			Math.pow(3.0 * curve.p2[0] - 2.0 * curve.p3[0] - curve.p0[0], 2.0)
		)
			+
			Math.max(
				Math.pow(3.0 * curve.p1[1] - 2.0 * curve.p0[1] - curve.p3[1], 2.0),
				Math.pow(3.0 * curve.p2[1] - 2.0 * curve.p3[1] - curve.p0[1], 2.0)
			);

		//		let ux = 3.0 * this.p1[0] - 2.0 * this.p0[0] - this.p3[0]; ux *= ux;
		//		let uy = 3.0 * this.p1[1] - 2.0 * this.p0[1] - this.p3[1]; uy *= uy;
		//		let vx = 3.0 * this.p2[0] - 2.0 * this.p3[0] - this.p0[0]; vx *= vx;
		//		let vy = 3.0 * this.p2[1] - 2.0 * this.p3[1] - this.p0[1]; vy *= vy;
		//		if (ux < vx) ux = vx;
		//		if (uy < vy) uy = vy;
		//		return ux + uy;
	}

	public toLinearApproximation(): number[][2] {
		return undefined;
	}

	public static linearize(curve: CubicBezierControlPoints, flatness: number): number[][] {
		if (CubicBezier.flatness(curve) <= 16 * flatness * flatness) {
			return [curve.p0, curve.p3];
		} else {
			let splittedCurve = CubicBezier.splitCurve(curve, 0.5);
			return CubicBezier.linearize(splittedCurve.c0, flatness).concat(
				CubicBezier.linearize(splittedCurve.c1, flatness).slice(1)
			);
		}
	}

	public static toSVGPath(curves: CubicBezierControlPoints[]): (string | number)[][] {
		let path: (string | number)[][] = [];
		if (curves && curves.length > 0) {
			path.push(["M", curves[0].p0[0], curves[0].p0[1]]);
			curves.forEach((curve: CubicBezierControlPoints) => {
				path.push(["C", curve.p1[0], curve.p1[1], curve.p2[0], curve.p2[1], curve.p3[0], curve.p3[1]]);
			})
		}
		return path;
	}

	/**
	 * Returns string representation.
	 */
	public toString(): string {
		return `CubicBezier{ p:[${this.p0}, ${this.p1}, p2:${this.p2}, p3:${this.p3}], dT:${this.dT}}`;
	}
}
